'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { sendEvent } from '../../browser/send-event';

import type {
  AutomaticModalTriggerConfig,
  ModalDisplayFrequency,
} from '../../core';

type DataLayerEvent = {
  event?: string;
  eventAction?: string;
  [key: string]: unknown;
};

type ExtendedWindow = Window & {
  faasBlockExitIntent?: boolean;
  launchPplModal?: boolean;
  allcaponeBlockExitIntent?: boolean;
  dataLayer?: DataLayerEvent[];
};

interface UseTriggeredModalProps {
  formId: string;
  linkedFormIds?: readonly string[];
  trigger: AutomaticModalTriggerConfig;
  displayFrequency?: ModalDisplayFrequency;
  allowedUrls?: readonly string[];
  restrictedUrls?: readonly string[];
  disabled?: boolean;
  skipModalTriggerCheck?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

const EXIT_INTENT_SHOWN_KEY = 'exitIntentShown';
const SCROLL_DEBOUNCE_MS = 50;
const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;
const REGEX_URL_PART_PATTERN = /^regex\('(.*)'\)$/;

const getFormStorageKey = (formId: string): string =>
  `FAAS_MODAL_${formId}_LAST_DISPLAY_TIME`;

const canUseLocalStorage = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    window.localStorage.getItem('FAAS_STORAGE_CHECK');
    return true;
  } catch {
    return false;
  }
};

const didDisplayModalTooLongAgo = (
  formId: string,
  displayFrequency: ModalDisplayFrequency
) => {
  if (displayFrequency === 'Every Time') {
    return true;
  }

  const timeOfLastModalDisplay = window.localStorage.getItem(
    getFormStorageKey(formId)
  );

  if (timeOfLastModalDisplay === null) {
    return true;
  }

  if (displayFrequency === 'Every 7 days') {
    const diffInMilliseconds =
      Date.now() - Number.parseInt(timeOfLastModalDisplay, 10);
    const diffInDays = diffInMilliseconds / MILLISECONDS_PER_DAY;
    return diffInDays >= 7;
  }

  return false;
};

const shouldDisplayModal = (
  formIds: string[],
  displayFrequency: ModalDisplayFrequency
) => {
  if (displayFrequency === 'Every Time') {
    return true;
  }

  if (!canUseLocalStorage()) {
    return false;
  }

  return formIds.every((id) => didDisplayModalTooLongAgo(id, displayFrequency));
};

const markModalDisplayed = (formIds: string[]) => {
  if (canUseLocalStorage()) {
    const currentTimestamp = Date.now().toString();

    formIds.forEach((id) => {
      window.localStorage.setItem(getFormStorageKey(id), currentTimestamp);
    });

    window.localStorage.setItem(EXIT_INTENT_SHOWN_KEY, 'true');
  }
};

const matchUrlPart = (url: string, part: string) => {
  const regexMatch = part.match(REGEX_URL_PART_PATTERN);

  if (regexMatch?.[1]) {
    try {
      return new RegExp(regexMatch[1], 'i').test(url);
    } catch {
      // Malformed regex in config — treat as no match rather than crash the page
      return false;
    }
  }

  return url.toLowerCase().includes(part.toLowerCase());
};

const isCurrentUrlAllowed = (
  allowedUrls: readonly string[] | undefined,
  url: string
) => {
  if (!Array.isArray(allowedUrls) || allowedUrls.length === 0) {
    return true;
  }

  return allowedUrls.some((allowedUrlPart) =>
    matchUrlPart(url, allowedUrlPart)
  );
};

const isCurrentUrlRestricted = (
  restrictedUrls: readonly string[] | undefined,
  url: string
) =>
  Array.isArray(restrictedUrls) &&
  restrictedUrls.some((restrictedUrlPart) =>
    matchUrlPart(url, restrictedUrlPart)
  );

const isCookieConsentBannerOpen = () => {
  const oneTrustScript = window.document.querySelector(
    'script[src*="cookielaw.org"]'
  );
  if (
    oneTrustScript &&
    !window.document.getElementById('onetrust-consent-sdk')
  ) {
    return true;
  }

  const cookieBanner = window.document.querySelector(
    '#onetrust-banner-sdk'
  ) as HTMLDivElement | null;

  return Boolean(cookieBanner && cookieBanner.style.display !== 'none');
};

const isModalBlocked = () => {
  const extendedWindow = window as unknown as ExtendedWindow;

  if (window.document.body?.style?.overflow === 'hidden') {
    return true;
  }

  if (extendedWindow.faasBlockExitIntent) {
    return true;
  }

  if (extendedWindow.launchPplModal) {
    return true;
  }

  if (extendedWindow.allcaponeBlockExitIntent) {
    return true;
  }

  const dataLayer: DataLayerEvent[] = extendedWindow.dataLayer ?? [];
  const hasConversionEvent = dataLayer.some(
    (event) =>
      event &&
      (event.event === 'sendConversion' || event.eventAction === 'Converted')
  );

  if (hasConversionEvent) {
    return true;
  }

  if (window.document.querySelector('.modal-form-container')) {
    return true;
  }

  return false;
};

const getScrollPercent = () => {
  const { documentElement: docEl, body } = window.document;
  const scrollTop = docEl.scrollTop || body.scrollTop;
  const scrollHeight = docEl.scrollHeight || body.scrollHeight;
  const { clientHeight } = docEl;

  return (scrollTop / (scrollHeight - clientHeight)) * 100;
};

export function useTriggeredModal({
  formId,
  linkedFormIds = [],
  trigger,
  displayFrequency = 'Every 7 days',
  allowedUrls,
  restrictedUrls,
  disabled = false,
  skipModalTriggerCheck = false,
  onOpen,
  onClose,
}: UseTriggeredModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const didSetExitIntentBlock = useRef(false);

  const formIds = useMemo(
    () => Array.from(new Set([formId, ...linkedFormIds])),
    [formId, linkedFormIds]
  );

  const openModal = useCallback(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    if (disabled) {
      return false;
    }

    if (!shouldDisplayModal(formIds, displayFrequency)) {
      return false;
    }

    const currentUrl = window.location.href;

    if (!isCurrentUrlAllowed(allowedUrls, currentUrl)) {
      return false;
    }

    if (isCurrentUrlRestricted(restrictedUrls, currentUrl)) {
      return false;
    }

    if (isCookieConsentBannerOpen()) {
      return false;
    }

    const canOpen = skipModalTriggerCheck || !isModalBlocked();

    if (!canOpen) {
      return false;
    }

    const extendedWindow = window as unknown as ExtendedWindow;

    markModalDisplayed(formIds);
    extendedWindow.faasBlockExitIntent = true;
    didSetExitIntentBlock.current = true;
    setIsOpen(true);
    onOpen?.();

    return true;
  }, [
    allowedUrls,
    disabled,
    displayFrequency,
    formIds,
    onOpen,
    restrictedUrls,
    skipModalTriggerCheck,
  ]);

  const closeModal = useCallback(() => {
    const extendedWindow = window as unknown as ExtendedWindow;

    if (didSetExitIntentBlock.current) {
      extendedWindow.faasBlockExitIntent = false;
      didSetExitIntentBlock.current = false;
    }

    setIsOpen(false);
    onClose?.();
  }, [onClose]);

  useEffect(
    () => () => {
      const extendedWindow = window as unknown as ExtendedWindow;

      if (didSetExitIntentBlock.current) {
        extendedWindow.faasBlockExitIntent = false;
        didSetExitIntentBlock.current = false;
      }
    },
    []
  );

  useEffect(() => {
    if (disabled || typeof window === 'undefined') {
      return undefined;
    }

    if (!shouldDisplayModal(formIds, displayFrequency)) {
      return undefined;
    }

    if (trigger.type === 'scroll-depth') {
      let scrollTimeoutId: number | undefined;

      const handleScroll = () => {
        if (scrollTimeoutId !== undefined) {
          window.clearTimeout(scrollTimeoutId);
        }

        scrollTimeoutId = window.setTimeout(() => {
          if (getScrollPercent() < trigger.scrollDepth) {
            return;
          }

          if (trigger.trackScrollReached) {
            sendEvent({
              name: 'form_scroll_depth_reached',
            });
          }

          openModal();
          window.removeEventListener('scroll', handleScroll);
        }, SCROLL_DEBOUNCE_MS);
      };

      window.addEventListener('scroll', handleScroll, {
        passive: true,
      });

      return () => {
        if (scrollTimeoutId !== undefined) {
          window.clearTimeout(scrollTimeoutId);
        }

        window.removeEventListener('scroll', handleScroll);
      };
    }

    if (trigger.type === 'time-on-page') {
      const timeoutId = window.setTimeout(() => {
        openModal();
      }, trigger.delayMs);

      return () => {
        window.clearTimeout(timeoutId);
      };
    }

    let exitIntentTimeoutId: number | undefined;

    const clearPendingExitIntent = () => {
      if (exitIntentTimeoutId !== undefined) {
        window.clearTimeout(exitIntentTimeoutId);
        exitIntentTimeoutId = undefined;
      }
    };

    const handleMouseOut = (event: MouseEvent) => {
      if (isModalBlocked()) {
        return;
      }

      if (event.clientY >= 0 || exitIntentTimeoutId !== undefined) {
        return;
      }

      exitIntentTimeoutId = window.setTimeout(() => {
        exitIntentTimeoutId = undefined;
        openModal();
      }, trigger.delayMs ?? 0);

      window.addEventListener('mouseover', clearPendingExitIntent, {
        once: true,
      });
    };

    document.documentElement.addEventListener('mouseout', handleMouseOut);

    return () => {
      clearPendingExitIntent();
      document.documentElement.removeEventListener('mouseout', handleMouseOut);
    };
  }, [disabled, displayFrequency, formIds, openModal, trigger]);

  return { closeModal, isOpen, openModal };
}
